import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();

  const navigation = [
    { name: "Home", href: "/" },
    { name: "Services", href: "/services" },
    { name: "How It Works", href: "/how-it-works" },
    { name: "Forms Library", href: "/forms-library" },
    { name: "Investment Advisor", href: "/investment-advisor" },
    { name: "Enterprise Hub", href: "/enterprise-hub" },
    { name: "Pricing", href: "/pricing" },
    { name: "About Us", href: "/about" },
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="text-lg font-bold">
              <span className="text-teal-600">Form</span>
              <span className="text-teal-800">Forge</span>
              <span className="text-teal-600">Pro</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-colors duration-200 hover:text-blue-900 ${
                  isActive(item.href)
                    ? "text-blue-900 border-b-2 border-blue-900 pb-1"
                    : "text-gray-700"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <div className="flex items-center space-x-2 text-gray-700">
                  <User className="w-4 h-4" />
                  <span>Hi, {user?.first_name}!</span>
                </div>
                <Button 
                  variant="ghost"
                  asChild
                  className="text-gray-700 hover:text-gray-900 font-medium"
                >
                  <Link to="/dashboard">Dashboard</Link>
                </Button>
                <Button 
                  variant="outline"
                  onClick={logout}
                  className="text-gray-700 hover:text-gray-900 font-medium"
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="ghost"
                  asChild
                  className="text-teal-600 hover:text-teal-800 font-medium"
                >
                  <Link to="/signin">Sign In</Link>
                </Button>
                <Button 
                  asChild
                  className="bg-teal-500 hover:bg-teal-600 text-white font-semibold px-6 py-2 rounded-lg transition-all duration-200 hover:shadow-lg"
                >
                  <Link to="/services">Get Started</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100">
            <div className="flex flex-col space-y-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm font-medium transition-colors duration-200 hover:text-blue-900 px-2 py-1 ${
                    isActive(item.href) ? "text-blue-900 bg-orange-50 rounded" : "text-gray-700"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <Button 
                asChild
                className="bg-teal-500 hover:bg-teal-600 text-white font-semibold px-6 py-2 rounded-lg w-full mt-4"
              >
                <Link to="/services" onClick={() => setIsMenuOpen(false)}>
                  Get Started
                </Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;